import { entries, isNil } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'react-use';
import { Button, Monitor, TextInput } from 'react95';
import { subscribeTaskListSocket } from '../../actions/socketActions';
import {
  setBulkEditMode,
  setBulkEditTasks,
  setTaskListFilters,
  setTaskListShowAll,
  setTaskListCurrentTab,
  setTaskListYear
} from '../../actions/taskListActions';
import { AVAILABLE_TAX_YEARS, TASK_SUBSTAGES } from '../../global/Environment';
import { userSelector } from '../../selectors/authSelectors';
import {
  bulkEditModeSelector,
  bulkEditTasksSelector,
  showAllReturnsSelector,
  taskListCurrentTabSelector,
  taskListFiltersSelector,
  taskListOrderSelector,
  taskListPageNumberSelector,
  yearSelector
} from '../../selectors/taskListSelectors';
import { isCurrentUserAdmin } from '../../utils/taskUtils';
import QDeckToolBar from '../Common/QDeckToolBar';
import BulkEditModal from './BulkEditModal';
import AddTaskModal from './AddTaskModal';
import './TaskList.scss';
import TaskListSidePanel from './TaskListSidePanel';
import TaskListTable from './TaskListTable';
import { useFetchQDeckUsersQuery } from '../../api/qdeckApi';
import { opsSystemApi, useGetTasksQuery, useGetTaskYearCountsQuery } from '../../api/opsSystemApi';
import { TAGS } from '../../api/baseApi';
import { TASK_LIST_TABS } from '../../constants';
import { Box, Stack, Typography } from '@mui/material';

const TaskList = () => {
  const dispatch = useDispatch();

  const [currentSearchValue, setCurrentSearchValue] = useState(null);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);

  const user = useSelector(userSelector);
  const year = useSelector(yearSelector);
  const showAllReturns = useSelector(showAllReturnsSelector);
  const order = useSelector(taskListOrderSelector);
  const bulkEditMode = useSelector(bulkEditModeSelector);
  const bulkEditTasks = useSelector(bulkEditTasksSelector);
  const filters = useSelector(taskListFiltersSelector);
  const currentTab = useSelector(taskListCurrentTabSelector);
  const pageNumber = useSelector(taskListPageNumberSelector);

  const { data: qdeckUsers = [], isLoading: areQDeckUsersLoading } = useFetchQDeckUsersQuery();
  const isAdmin = isCurrentUserAdmin(user.email, qdeckUsers);

  const { data: tasks = [], isLoading: areTasksLoading } = useGetTasksQuery(
    {
      year,
      showAllReturns,
      order,
      filters,
      pageNumber
    },
    {
      skip: areQDeckUsersLoading
    }
  );
  const { data: yearCounts = [], isLoading: areYearCountsLoading } = useGetTaskYearCountsQuery();

  const TABS = [
    {
      key: TASK_LIST_TABS.ALL_TASKS,
      name: 'All tasks',
      onClick: () => {
        dispatch(setTaskListFilters({ search: filters.search }));
        dispatch(setTaskListCurrentTab(TASK_LIST_TABS.ALL_TASKS));
      }
    },
    {
      key: TASK_LIST_TABS.SUBSTAGE_TASKS,
      name: 'Substage tasks',
      onClick: () => {
        dispatch(
          setTaskListFilters({
            search: filters.search,
            substage: TASK_SUBSTAGES.PREMIUM_OPS_REVIEW
          })
        );
        dispatch(setTaskListCurrentTab(TASK_LIST_TABS.SUBSTAGE_TASKS));
      }
    },
    {
      key: TASK_LIST_TABS.ASSIGNED_TO_ME,
      name: 'Assigned to me',
      onClick: () => {
        dispatch(setTaskListFilters({ search: filters.search, assignee: user.email }));
        dispatch(setTaskListCurrentTab(TASK_LIST_TABS.ASSIGNED_TO_ME));
      }
    }
  ];

  useEffect(() => {
    // set websocket connection
    dispatch(subscribeTaskListSocket());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTaxYearSelect = (currentYear, selectedYear) => {
    if (currentYear !== selectedYear) {
      dispatch(setTaskListYear(selectedYear));
      resetTable();
    }
  };

  /** Trigger a search based on the current input value */
  const triggerSearch = () => {
    if (!isNil(currentSearchValue)) {
      handleShowAllReturnsToggle(true);
      dispatch(setTaskListFilters({ ...filters, search: currentSearchValue }));
    }
  };

  const [, cancelDebounce] = useDebounce(triggerSearch, 500, [currentSearchValue]);

  const handleShowAllReturnsToggle = (showAll) => {
    dispatch(setTaskListShowAll(showAll));
  };

  // Remove all filters and show all tasks upon searching
  const handleSearchInput = (value) => {
    setCurrentSearchValue(value);
  };

  /** Cancel pending debounce and immediately trigger a search */
  const flushSearch = () => {
    cancelDebounce();
    triggerSearch();
  };

  const resetTable = () => {
    const newFilters = {
      ...(currentTab === TASK_LIST_TABS.ASSIGNED_TO_ME ? { assignee: user.email } : {}),
      ...(currentTab === TASK_LIST_TABS.SUBSTAGE_TASKS ? { substage: TASK_SUBSTAGES.PREMIUM_OPS_REVIEW } : {})
    };
    dispatch(setTaskListFilters(newFilters));
    setCurrentSearchValue(null);
    handleShowAllReturnsToggle(false);
  };

  const menuItems = AVAILABLE_TAX_YEARS.map((taxYear) => ({
    name: taxYear,
    primary: year === taxYear,
    onClick: () => onTaxYearSelect(year, taxYear)
  }));

  const tabs = TABS.map(({ key, name, onClick }) => ({
    name,
    active: currentTab === key,
    onClick: () => onClick()
  }));

  const searchItem = (
    <TextInput
      multiline={false}
      placeholder='Search by userId, phone, ssn, email, or name'
      value={currentSearchValue || ''}
      onChange={(e) => handleSearchInput(e.target.value)}
      onBlur={flushSearch}
      onKeyDown={(e) => e.key === 'Enter' && flushSearch()}
      data-testid='task-list-search-input'
      fullWidth
    />
  );

  const isLoading = areQDeckUsersLoading || areTasksLoading || areYearCountsLoading;

  /** Flag to determine whether the "Clear filters" button should be disabled */
  const isClearFiltersButtonDisabled = useMemo(() => {
    const filtersWithNullValuesRemoved = entries(filters).filter(([_, value]) => !isNil(value));
    if (isLoading) {
      return true;
    } else if (currentTab === TASK_LIST_TABS.ALL_TASKS) {
      // For the ALL_TASKS tab, the "Clear filters" button is disabled if:
      // - there are no filters AND
      // - the showAllReturns checkbox is unchecked
      return filtersWithNullValuesRemoved.length === 0 && !showAllReturns;
    } else if (currentTab === TASK_LIST_TABS.SUBSTAGE_TASKS) {
      // For the SUBSTAGE_TASKS tab, the "Clear filters" button is disabled if:
      // - there is only one filter AND
      // - that filter is set to the PREMIUM_OPS_REVIEW substage (the default) AND
      // - the showAllReturns checkbox is unchecked
      return (
        filtersWithNullValuesRemoved.length === 1 &&
        filters.substage === TASK_SUBSTAGES.PREMIUM_OPS_REVIEW &&
        !showAllReturns
      );
    } else if (currentTab === TASK_LIST_TABS.ASSIGNED_TO_ME) {
      // For the ASSIGNED_TO_ME tab, the "Clear filters" button is disabled if:
      // - there is only one filter (the assignee filter for the current user) AND
      // - the showAllReturns checkbox is unchecked
      return filtersWithNullValuesRemoved.length === 1 && !showAllReturns;
    }
    return false;
  }, [filters, currentTab, showAllReturns, isLoading]);

  return (
    <div className='task-list'>
      <QDeckToolBar menuItems={menuItems} searchItem={searchItem} tabs={tabs} />

      <div className='task-list-content'>
        <div>
          <div className='task-list-content-monitor'>
            <Monitor backgroundStyles={{ background: 'black' }}>
              <Box
                sx={{
                  ml: 1,
                  px: 1,
                  color: 'lime',
                  height: '100%',
                  overflowY: 'scroll',
                  '&::-webkit-scrollbar': {
                    width: '8px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgb(190, 190, 190)',
                    borderRadius: '4px'
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'rgb(160, 160, 160)'
                  }
                }}
              >
                {Object.entries(yearCounts)
                  .sort(([yearA], [yearB]) => yearB - yearA)
                  .map(([year, count]) => (
                    <Stack key={year} direction='row'>
                      <Typography sx={{ fontSize: 'large', mr: 1 }}>{year}:</Typography>
                      <Typography sx={{ fontSize: 'large', fontWeight: 'bold' }}>{count.toLocaleString()}</Typography>
                    </Stack>
                  ))}
              </Box>
            </Monitor>
            {isNil(bulkEditMode) ? (
              <div className='task-list-content-monitor-btns'>
                <Button
                  disabled={isLoading}
                  primary
                  onClick={() =>
                    dispatch(
                      opsSystemApi.util.invalidateTags([TAGS.TASK_LIST, TAGS.SUBSTAGE_COUNTS, TAGS.STAGE_COUNTS])
                    )
                  }
                >
                  <span>Refresh</span>
                </Button>
                <Button disabled={isClearFiltersButtonDisabled} onClick={resetTable}>
                  <span>Clear filters</span>
                </Button>
                <Button disabled={isLoading} onClick={() => dispatch(setBulkEditMode('select'))}>
                  <span>Override edit</span>
                </Button>
                <Button disabled={isLoading} onClick={() => setIsAddTaskOpen(true)}>
                  <span>Add task</span>
                </Button>
              </div>
            ) : bulkEditMode === 'select' ? (
              <div className='task-list-content-monitor-btns'>
                {isAdmin && (
                  <>
                    <div>
                      <div>Selected tasks:</div>
                      <div>{bulkEditTasks?.length || 0}</div>
                    </div>

                    <Button
                      className='bulk-edit-confirm'
                      primary
                      disabled={!bulkEditTasks?.length}
                      onClick={() => dispatch(setBulkEditMode('selected'))}
                    >
                      <span>Override edit</span>
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => {
                    dispatch(setBulkEditTasks([]));
                    dispatch(setBulkEditMode(null));
                  }}
                >
                  <span>Cancel</span>
                </Button>
              </div>
            ) : null}
          </div>

          <TaskListSidePanel onShowAllReturnsToggle={handleShowAllReturnsToggle} />
        </div>

        <Stack sx={{ margin: 1, alignSelf: 'stretch', overflowX: 'auto' }}>
          <TaskListTable isAdmin={isAdmin} tasks={tasks} areTasksLoading={areTasksLoading} />
        </Stack>
      </div>

      {(bulkEditMode === 'all' || bulkEditMode === 'selected') && (
        <BulkEditModal tasks={tasks} areTasksLoading={areTasksLoading} />
      )}
      {isAddTaskOpen && <AddTaskModal onClose={() => setIsAddTaskOpen(false)} />}
    </div>
  );
};

export default TaskList;
